/* ============================================================
   CadenceSvgFilters.jsx — site-wide SVG filter defs
   ------------------------------------------------------------
   Houses the signature transition filters used across CADENCE.

   #cadence-glitter-dust (founder spec 2026-02-08 "glittery sparkly,
     not a wavy flashback — like glittery wave that goes INTO the
     image"). The filter SCATTERS tiny gold-foil specks across the
     subject while leaving the source pixels untouched.

   How the math works:
     1) feTurbulence generates a high-frequency fractalNoise field.
        baseFrequency + seed both animate so the dots TWINKLE.
     2) feColorMatrix pushes ONLY the brightest 5-10% of noise to a
        warm foil-gold (R=1, G=0.87, B=0.30). The 4th row
        "k 0 0 0 -k+ε" hard-cuts the alpha so most pixels become
        fully transparent and only the brightest specks survive as
        gold sparkles. result="sparkleDust".
     3) feComposite "over" lays the gold sparkle dust ON TOP of the
        source graphic.

   The filter is mounted once globally in App.js so the id is
   available across the entire site (Stats Reel cross-dissolve,
   founders video modal open, any future luxe transition).
   ============================================================ */

const CadenceSvgFilters = () => (
  <svg
    className="cadence-svg-filters"
    aria-hidden="true"
    focusable="false"
    width="0"
    height="0"
    style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }}
    data-testid="cadence-svg-filters"
  >
    <defs>
      <filter
        id="cadence-glitter-dust"
        x="-5%"
        y="-5%"
        width="110%"
        height="110%"
        colorInterpolationFilters="sRGB"
      >
        {/* Step 1 — animated high-frequency noise field. Slowed
            twinkle (2.4s cycle) reads as a luxe shimmer, not a
            frantic flicker. Lower baseFrequency = larger, more
            visible sparkle blobs (was 1.8 — looked like fine grit). */}
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.9"
          numOctaves="2"
          seed="11"
          stitchTiles="stitch"
          result="noise"
        >
          <animate
            attributeName="baseFrequency"
            dur="2.4s"
            values="0.9; 1.1; 0.8; 1.0; 0.9"
            repeatCount="indefinite"
          />
          <animate
            attributeName="seed"
            dur="0.9s"
            values="11; 31; 73; 19; 11"
            repeatCount="indefinite"
          />
        </feTurbulence>

        {/* Step 2 — gentler alpha threshold = DENSER sparkle field
            (founder spec 2026-02-08 "I didn't notice the sparkle").
            Was 18/-16 → only the brightest 5% survived; now 11/-9
            keeps ~12-15% of noise pixels as gold sparkles. Recolour
            to warm foil-gold (R=1, G=0.87, B=0.30). */}
        <feColorMatrix
          in="noise"
          type="matrix"
          values="0 0 0 0 1
                  0 0 0 0 0.87
                  0 0 0 0 0.30
                  11 0 0 0 -9"
          result="sparkleDust"
        />

        {/* Step 3 — gold sparkles laid on top of the source pixels. */}
        <feComposite in="sparkleDust" in2="SourceGraphic" operator="over" />
      </filter>
    </defs>
  </svg>
);

export default CadenceSvgFilters;
